// =============================================================================
// FE-18 — Delegation Error State (shared)
// Reusable error display used across all delegation pages and components.
// Provides three visual modes:
//   - 'alert'  — inline Alert bar with retry button (default, minimal footprint)
//   - 'card'   — full Card with centred error message and retry (for page-level)
//   - 'inline' — plain text + retry, no container (for embedding in tables etc.)
// =============================================================================

import React from 'react';
import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type ErrorMode = 'alert' | 'card' | 'inline';

interface DelegationErrorStateProps {
  /** Short human-readable message explaining what failed */
  message?: string;
  /** Optional detail / secondary line */
  detail?: string;
  /** Callback for the retry button — if omitted the button is hidden */
  onRetry?: () => void;
  /** Visual mode */
  mode?: ErrorMode;
  /** Whether the retry action is in progress */
  retrying?: boolean;
  /** Additional className */
  className?: string;
}

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

// ---------------------------------------------------------------------------
// Alert mode (default — inline banner at top of content area)
// ---------------------------------------------------------------------------

function AlertError({
  message = DEFAULT_MESSAGE,
  onRetry,
  retrying,
  className,
}: DelegationErrorStateProps) {
  return (
    <Alert variant="destructive" className={className}>
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription className="flex items-center justify-between gap-4">
        <span>{message}</span>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            disabled={retrying}
            className="shrink-0"
          >
            {retrying ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            Retry
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Card mode (centred, for page-level / full-area errors)
// ---------------------------------------------------------------------------

function CardError({
  message = DEFAULT_MESSAGE,
  detail,
  onRetry,
  retrying,
  className,
}: DelegationErrorStateProps) {
  return (
    <Card className={className}>
      <CardContent className="flex flex-col items-center justify-center py-14 px-6 text-center">
        {/* Icon */}
        <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <WifiOff className="h-7 w-7 text-destructive" />
        </div>

        {/* Text */}
        <h3 className="text-base font-semibold mb-1.5">{message}</h3>
        {detail && (
          <p className="text-sm text-muted-foreground max-w-sm">{detail}</p>
        )}

        {/* Retry */}
        {onRetry && (
          <Button
            size="sm"
            variant="outline"
            onClick={onRetry}
            disabled={retrying}
            className="mt-6"
          >
            {retrying ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            Try Again
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Inline mode (plain, for use inside tables or compact areas)
// ---------------------------------------------------------------------------

function InlineError({
  message = DEFAULT_MESSAGE,
  onRetry,
  retrying,
  className,
}: DelegationErrorStateProps) {
  return (
    <div className={`flex items-center gap-3 text-sm text-destructive py-4 px-2 ${className ?? ''}`}>
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1">{message}</span>
      {onRetry && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}
          disabled={retrying}
          className="shrink-0 text-destructive hover:text-destructive"
        >
          {retrying ? (
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          ) : (
            'Retry'
          )}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export — renders correct mode
// ---------------------------------------------------------------------------

export const DelegationErrorState: React.FC<DelegationErrorStateProps> = ({
  mode = 'alert',
  ...props
}) => {
  switch (mode) {
    case 'card':
      return <CardError {...props} />;
    case 'inline':
      return <InlineError {...props} />;
    case 'alert':
    default:
      return <AlertError {...props} />;
  }
};
